/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {AnimationPlayer} from '@angular/animations';

import {copyStyles, eraseStyles, setStyles} from '../../util';

import {DOMAnimation} from './dom_animation';
export class WebAnimationsPlayer implements AnimationPlayer {
private _onDoneFns: Function[] = [];
private _onStartFns: Function[] = [];
private _onDestroyFns: Function[] = [];
private _player: DOMAnimation;
private _duration: number;
private _delay: number;
private _initialized = false;
private _finished = false;
private _started = false;
private _destroyed = false;
private _finalKeyframe: {[key: string]: string | number};
public time = 0;
public parentPlayer: AnimationPlayer|null = null;
public previousStyles: {[styleName: string]: string | number};
public currentSnapshot: {[styleName: string]: string | number} = {};
/**
 * @param {?} element
 * @param {?} keyframes
 * @param {?} options
 * @param {?=} previousPlayers
 */
constructor(
public element: any,
public keyframes: {[key: string]: string | number}[],
public options: {[key: string]: string | number},
private previousPlayers: WebAnimationsPlayer[] = []) {
    this._duration = <number>options['duration'];
    this._delay = <number>options['delay'] || 0;
    this.time = this._duration + this._delay;

    this.previousStyles = {};
    previousPlayers.forEach(player => {
      let styles = player.currentSnapshot;
      Object.keys(styles).forEach(prop => this.previousStyles[prop] = styles[prop]);
    });
  }
/**
 * @return {?}
 */
private _onFinish() {
    if (!this._finished) {
      this._finished = true;
      this._onDoneFns.forEach(fn => fn());
      this._onDoneFns = [];
    }
  }
/**
 * @return {?}
 */
init(): void {
    this._buildPlayer();
    this._preparePlayerBeforeStart();
  }
/**
 * @return {?}
 */
private _buildPlayer(): void {
    if (this._initialized) return;
    this._initialized = true;

    const /** @type {?} */ keyframes = this.keyframes.map(styles => copyStyles(styles, false));
    const /** @type {?} */ previousStyleProps = Object.keys(this.previousStyles);
    if (previousStyleProps.length) {
      let /** @type {?} */ startingKeyframe = keyframes[0];
      let /** @type {?} */ missingStyleProps: string[] = [];
      previousStyleProps.forEach(prop => {
        if (!startingKeyframe.hasOwnProperty(prop)) {
          missingStyleProps.push(prop);
        }
        startingKeyframe[prop] = this.previousStyles[prop];
      });

      if (missingStyleProps.length) {
        const /** @type {?} */ self = this;
        // tslint:disable-next-line
        for (var /** @type {?} */ i = 1; i < keyframes.length; i++) {
          let /** @type {?} */ kf = keyframes[i];
          missingStyleProps.forEach(function(prop) {
            kf[prop] = _computeStyle(self.element, prop);
          });
        }
      }
    }

    this._player = this._triggerWebAnimation(this.element, keyframes, this.options);
    this._finalKeyframe = keyframes.length ? keyframes[keyframes.length - 1] : {};
    this._player.addEventListener('finish', () => this._onFinish());
  }
/**
 * @return {?}
 */
private _preparePlayerBeforeStart() {
    // this is required so that the player doesn't start to animate right away
    if (this._delay) {
      this._resetDomPlayerState();
    } else {
      this._player.pause();
    }
  }
/**
 * \@internal
 * @param {?} element
 * @param {?} keyframes
 * @param {?} options
 * @return {?}
 */
_triggerWebAnimation(element: any, keyframes: any[], options: any): DOMAnimation {
    // jscompiler doesn't seem to know animate is a native property because it's not fully
    // supported yet across common browsers (we polyfill it for Edge/Safari) [CL #143630929]
    return /** @type {?} */(( element['animate'](keyframes, options) as DOMAnimation));
  }
/**
 * @return {?}
 */
get domPlayer() { return this._player; }
/**
 * @param {?} fn
 * @return {?}
 */
onStart(fn: () => void): void { this._onStartFns.push(fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDone(fn: () => void): void { this._onDoneFns.push(fn); }
/**
 * @param {?} fn
 * @return {?}
 */
onDestroy(fn: () => void): void { this._onDestroyFns.push(fn); }
/**
 * @return {?}
 */
play(): void {
    this._buildPlayer();
    if (!this.hasStarted()) {
      this._onStartFns.forEach(fn => fn());
      this._onStartFns = [];
      this._started = true;
    }
    this._player.play();
  }
/**
 * @return {?}
 */
pause(): void {
    this.init();
    this._player.pause();
  }
/**
 * @return {?}
 */
finish(): void {
    this.init();
    this._onFinish();
    this._player.finish();
  }
/**
 * @return {?}
 */
reset(): void {
    this._resetDomPlayerState();
    this._destroyed = false;
    this._finished = false;
    this._started = false;
  }
/**
 * @return {?}
 */
private _resetDomPlayerState() {
    if (this._player) {
      this._player.cancel();
    }
  }
/**
 * @return {?}
 */
restart(): void {
    this.reset();
    this.play();
  }
/**
 * @return {?}
 */
hasStarted(): boolean { return this._started; }
/**
 * @return {?}
 */
destroy(): void {
    if (!this._destroyed) {
      this._resetDomPlayerState();
      this._onFinish();
      this._destroyed = true;
      this._onDestroyFns.forEach(fn => fn());
      this._onDestroyFns = [];
    }
  }
/**
 * @param {?} p
 * @return {?}
 */
setPosition(p: number): void { this._player.currentTime = p * this.time; }
/**
 * @return {?}
 */
getPosition(): number { return this._player.currentTime / this.time; }
/**
 * @return {?}
 */
get totalTime(): number { return this._delay + this._duration; }
/**
 * @return {?}
 */
beforeDestroy() {
    const /** @type {?} */ styles: {[key: string]: string | number} = {};
    if (this.hasStarted()) {
      Object.keys(this._finalKeyframe).forEach(prop => {
        if (prop != 'offset') {
          styles[prop] =
              this._finished ? this._finalKeyframe[prop] : _computeStyle(this.element, prop);
        }
      });
    }
    this.currentSnapshot = styles;
  }
}

function WebAnimationsPlayer_tsickle_Closure_declarations() {
/** @type {?} */
WebAnimationsPlayer.prototype._onDoneFns;
/** @type {?} */
WebAnimationsPlayer.prototype._onStartFns;
/** @type {?} */
WebAnimationsPlayer.prototype._onDestroyFns;
/** @type {?} */
WebAnimationsPlayer.prototype._player;
/** @type {?} */
WebAnimationsPlayer.prototype._duration;
/** @type {?} */
WebAnimationsPlayer.prototype._delay;
/** @type {?} */
WebAnimationsPlayer.prototype._initialized;
/** @type {?} */
WebAnimationsPlayer.prototype._finished;
/** @type {?} */
WebAnimationsPlayer.prototype._started;
/** @type {?} */
WebAnimationsPlayer.prototype._destroyed;
/** @type {?} */
WebAnimationsPlayer.prototype._finalKeyframe;
/** @type {?} */
WebAnimationsPlayer.prototype.time;
/** @type {?} */
WebAnimationsPlayer.prototype.parentPlayer;
/** @type {?} */
WebAnimationsPlayer.prototype.previousStyles;
/** @type {?} */
WebAnimationsPlayer.prototype.currentSnapshot;
/** @type {?} */
WebAnimationsPlayer.prototype.element;
/** @type {?} */
WebAnimationsPlayer.prototype.keyframes;
/** @type {?} */
WebAnimationsPlayer.prototype.options;
/** @type {?} */
WebAnimationsPlayer.prototype.previousPlayers;
}

/**
 * @param {?} element
 * @param {?} prop
 * @return {?}
 */
function _computeStyle(element: any, prop: string): string {
  return ( /** @type {?} */((<any>window.getComputedStyle(element))))[prop];
}
